import { useState } from "react";
import { ApiException } from "../../../../../services/api/ApiException";
import { toast } from "react-toastify";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { useConfirm } from "../../../../../hooks/useConfirm";
import vectorIcon from "../../../../../assets/_VectorIcon_.svg";
import { IProjectStatus } from "../../../../../@types/project/project-status.type";
import { ProjectStatusService } from "../../../../../services/api/projectStatus/ProjectStatusService";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import styles from "./styles.module.scss";

const schema = z.object({
  id: z.string().optional().nullable(),
  name: z
    .string()
    .min(1, "Nome do Status de Projeto é obrigatório")
    .max(60, "Nome do Status de Projeto deve ter no máximo 60 caracteres"),
  description: z
    .string()
    .max(
      500,
      "Descrição do Status de Projeto deve ter no máximo 500 caracteres"
    )
    .optional()
    .nullable(),
  isVisible: z.boolean(),
  isCompleted: z.boolean(),
});

interface FormProjectStatusProps {
  pageType: "create" | "update" | "seeMore";
  projectStatusData?: IProjectStatus;
}

export const FormProjectStatus: React.FC<FormProjectStatusProps> = ({
  pageType,
  projectStatusData,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IProjectStatus>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: projectStatusData?.id || undefined,
      name: projectStatusData?.name || "",
      description: projectStatusData?.description || "",
      order: projectStatusData?.order || 0,
      isVisible:
        projectStatusData?.isVisible === undefined
          ? true
          : projectStatusData.isVisible,
      isCompleted:
        projectStatusData?.isCompleted === undefined
          ? true
          : projectStatusData.isCompleted,
    },
  });

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);

  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir o Status de Projeto?",
    openModalConfirm,
    handleCloseModalConfirm
  );

  const navigate = useNavigate();

  const onSubmit = async (data: IProjectStatus) => {
    try {
      setLoading(true);
      data.order = projectStatusData?.order;
      await ProjectStatusService.addOrUpdateProjectStatus(data);
      reset();
      toast.success("Status de Projeto salvo com sucesso!");
      navigate("/configurations/projectstatus");
    } catch (error: any) {
      toast.error("Erro ao salvar Status de Projeto");
      new ApiException(error.message || "Erro ao processar a requisição.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    handleOpenModalConfirm();
    const isConfirm = await showConfirm();

    if (isConfirm) {
      try {
        await ProjectStatusService.deleteProjectStatus(
          projectStatusData?.id as string
        );
        toast.success("Status de Projeto excluído com sucesso!");
        navigate("/configurations/projectstatus");
      } catch (error) {
        console.log(error);
        toast.error(
          " erro ao excluir o Status de Projeto! Tente novamente mais tarde."
        );
      }
    }
  };

  return (
    <>
      <div className={styles.back}>
        <Button
          className={styles.button}
          onClick={() => navigate("/configurations/projectstatus")}
        >
          {vectorIcon && <img src={vectorIcon} />}
          Voltar a tela anterior
        </Button>
      </div>
      <div className={styles.title}>
        <h1>
          {pageType === "update"
            ? `${projectStatusData?.name}`
            : "Novo Status de Projeto"}
        </h1>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.formProjectStatus}
      >
        <div className={styles.fields}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                label="Nome do Status de Projeto"
                placeholder={
                  pageType === "create"
                    ? "Insira o nome do Status de Projeto aqui..."
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.name}
                helperText={errors.name?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                label="Descrição do Status de Projeto"
                placeholder={
                  pageType === "create"
                    ? "Insira a descrição do Status de Projeto aqui..."
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={(e) => {
                  const value = e.target.value;
                  setValue("description", value);
                }}
                error={!!errors.description}
                helperText={errors.description?.message}
                fullWidth
              />
            )}
          />

          <Controller
            name="isVisible"
            control={control}
            defaultValue={true}
            render={({ field }) => (
              <FormControlLabel
                style={{ flex: "none" }}
                control={
                  <Switch
                    checked={field.value}
                    onChange={(event) => field.onChange(event.target.checked)}
                  />
                }
                label="Visível no Portifólio"
              />
            )}
          />

          <Controller
            name="isCompleted"
            control={control}
            defaultValue={true}
            render={({ field }) => (
              <FormControlLabel
                style={{ flex: "none" }}
                control={
                  <Switch
                    checked={field.value}
                    onChange={(event) => field.onChange(event.target.checked)}
                  />
                }
                label="Finalizado"
              />
            )}
          />
        </div>

        <div className={styles.btnContainer}>
          {projectStatusData?.id && (
            <Button
              variant="contained"
              color="secondary"
              type="button"
              onClick={handleDeleteClick}
              className={styles.btnsDeleted}
              disabled={loading}
            >
              Excluir Status de Projeto
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={styles.btnsSave}
            disabled={loading}
          >
            Salvar
          </Button>
        </div>
      </form>
      {Confirm}
    </>
  );
};
